import {
  CONNECTING_TO_CONTENTFUL,
  CONNECTING_TO_REDIS,
  CookieKey,
  LANGUAGES,
  ROUTES,
  RouteName,
  ThunkDebugName,
} from '@/constants';
import { getPmtPage, getTranslationData } from '@/fetchers';
import { LanguageCode } from '@/interfaces';
import type { IPmtOnboardingPopup } from '@/interfaces/tracking';
import { PMT } from '@/modules/pmt';
import { shipmentSummaryRequiredGtmParams } from '@/modules/pmt/constants';
import type {
  IMainTranslation,
  INotificationTranslation,
  IPmtPage,
  ITrackingTranslation,
} from '@/modules/pmt/interfaces';
import { getGaClientId, getSessionId } from '@/modules/pmt/utils/ga4.utils';
import { checkIsPreviewModeRequest, detectMobileOS, getRandomData, replacePath } from '@/utils';
import createServerSidePropsResult, { type IThunk } from '@/utils/createServerSidePropsResult.utils';
import Cookies from 'js-cookie';
import type { GetServerSideProps, NextPage } from 'next';
import getConfig from 'next/config';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { createSMEShipment, updateShipmentLastSearched } from '../modules/pmt/fetchers/tracking.fetchers';

const { publicRuntimeConfig, serverRuntimeConfig } = getConfig();

const TrackMyParcelPage: NextPage<ITrackMyParcelProps> = ({ webVersion, ...props }) => {
  const { origin } = publicRuntimeConfig;
  const { query } = useRouter();
  // biome-ignore lint/suspicious/noConsoleLog: log webVersion
  console.log(webVersion);

  useEffect(() => {
    (async () => {
      // Handle redirect from Shopify (SME shipment)
      // TODO: should be protected by reCaptcha?
      if (query['isSME'] === 'true') {
        const { trackingPageReference, parcelId, orderId, carrierId, parcelperformId } = query;
        // TODO: get user id from global state instead of cookies
        const userId = Cookies.get(CookieKey.UserId);
        try {
          await createSMEShipment({
            userId,
            trackingPageReference: trackingPageReference as string,
            parcelId: parcelId as string,
            orderId: orderId as string,
            carrierId: carrierId as string,
            parcelperformId: parcelperformId as string,
            gaClientId: getGaClientId(),
            gaSessionId: getSessionId(true),
            eventTimestamp: Date.now().toString(),
          });
        } catch (error) {
          console.error('Could not create SME shipment', error);
        }
      }
    })();
  }, [query]);

  return (
    <>
      <Head>
        {LANGUAGES.map(({ code }) => {
          let path = ROUTES[RouteName.TrackMyParcelEn].path;
          if (code !== LanguageCode.English) path = replacePath(RouteName.TrackMyParcelMultiLanguages, [code]);

          return <link key={code} rel='alternate' hrefLang={code} href={`${origin}${path}`} />;
        })}
      </Head>
      <PMT {...props} />
    </>
  );
};

interface ITrackMyParcelServerSideProps {
  mainTranslation: IMainTranslation;
  trackingTranslation: ITrackingTranslation;
  notificationTranslation: INotificationTranslation;
  pmtPageData: IPmtPage | null;
  webVersion: string;
}

interface ITrackMyParcelProps extends ITrackMyParcelServerSideProps {
  pmtOnboardingPopUp: IPmtOnboardingPopup;
  pmtEmailSyncOnboardingPopUp: IPmtOnboardingPopup;
}

interface ITrackMyParcelParams extends NodeJS.Dict<string> {
  articleTypeSlug: string;
}

type Thunk = IThunk<ITrackMyParcelServerSideProps, ITrackMyParcelParams>;

const getRedisUtils: Thunk = async ({ nextJsContext, customContext }) => {
  if (!customContext.serverRuntimeConfig.useDev) {
    const redisUtils = await import('@/utils/redis.utils');

    customContext.getServerPropsCached = redisUtils.getServerPropsCached;
    customContext.cacheServerProps = redisUtils.cacheServerProps;
    customContext.setUrlById = redisUtils.setUrlById;
  }

  nextJsContext.res.setHeader('Cache-Control', 'public, s-maxage=43200, stale-while-revalidate=60');
};

getRedisUtils.debugName = ThunkDebugName.GetRedisUtils;

const getServerPropsCached: Thunk = async ({ nextJsContext, customContext }) => {
  const { resolvedUrl } = nextJsContext;
  customContext.pageURL = resolvedUrl.split('?')[0];

  const cacheData = await customContext.getServerPropsCached(customContext.pageURL);
  const serverPropsCached: ITrackMyParcelServerSideProps = cacheData ? JSON.parse(cacheData) : null;

  if (serverPropsCached) {
    return {
      props: {
        ...serverPropsCached,
        trackingTranslation: getRandomData<ITrackingTranslation>(
          serverPropsCached.trackingTranslation as ITrackingTranslation[],
        ),
        notificationTranslation: getRandomData(serverPropsCached.notificationTranslation as INotificationTranslation[]),
        webVersion: CONNECTING_TO_REDIS,
      },
    };
  }
};

getServerPropsCached.debugName = ThunkDebugName.GetServerPropsCached;

const getPageData: Thunk = async ({ nextJsContext, customContext }) => {
  const { query, params } = nextJsContext;
  const { serverSideProps, setUrlById, cacheServerProps, pageURL } = customContext;

  const languageCode = (params?.articleTypeSlug as LanguageCode) || LanguageCode.English;

  if (!Object.values<string>(LanguageCode).includes(languageCode)) {
    return {
      notFound: true,
    };
  }
  customContext.isPreviewMode = checkIsPreviewModeRequest(query);
  const isPreviewMode = customContext.isPreviewMode;

  serverSideProps.pmtPageData = await getPmtPage({
    isPreviewMode,
    languageCode,
  });

  if (serverSideProps.pmtPageData) {
    const { mainTranslation, trackingTranslations, notificationTranslations } = await getTranslationData({
      isPreviewMode,
      languageCode,
    });

    serverSideProps.mainTranslation = mainTranslation;
    // randomize translation data
    serverSideProps.trackingTranslation = getRandomData<ITrackingTranslation>(trackingTranslations);
    serverSideProps.notificationTranslation = getRandomData<INotificationTranslation>(notificationTranslations);

    if (!isPreviewMode) {
      setUrlById(serverSideProps.pmtPageData.sys.id, pageURL);
      cacheServerProps(pageURL, {
        ...serverSideProps,
        trackingTranslation: trackingTranslations,
        notificationTranslation: notificationTranslations,
      });
    }

    return {
      props: {
        ...serverSideProps,
      },
    };
  }

  return {
    props: {
      ...serverSideProps,
    },
    notFound: true,
  };
};

getPageData.debugName = 'GET_PAGE_DATA';

export const getServerSideProps: GetServerSideProps<ITrackMyParcelServerSideProps, ITrackMyParcelParams> = async (
  context,
) => {
  const { cookies, headers } = context.req;
  const isGoogleBot = (headers['user-agent'] || '').toLowerCase().includes('googlebot');

  // Add a crawler-friendly path for Google - simplified content
  if (isGoogleBot) {
    return createServerSidePropsResult<ITrackMyParcelServerSideProps, ITrackMyParcelParams>({
      context,
      customContext: {
        serverSideProps: {
          mainTranslation: {},
          trackingTranslation: {},
          notificationTranslation: {},
          pmtPageData: null,
          webVersion: CONNECTING_TO_CONTENTFUL,
        },
        serverRuntimeConfig,
        getServerPropsCached: () => null,
        cacheServerProps: () => null,
        setUrlById: () => null,
      },
      thunks: [getRedisUtils, getServerPropsCached, getPageData],
    });
  }

  // Handle redirect from shipment summary email
  if (shipmentSummaryRequiredGtmParams.every((val) => !!context.query[val])) {
    const userUuid = cookies[CookieKey.UserId];

    // [Guest user] Redirected from shipment summary page + Url not containing utm_source === 'shipment-summary'
    // Return proper 404 status instead of redirecting
    if (!userUuid && context.query['utm_source'] !== 'shipment-summary') {
      return { notFound: true };
    }

    // [LoggedIn] User used PMT and already logged in -> bump shipment last_searched
    if (userUuid) {
      try {
        await updateShipmentLastSearched({
          shipmentId: context.query['shipmentFsId'] as string,
          userUuid,
        });
      } catch (err) {
        console.error('Could not update shipment last_searched', err);
        if (err instanceof Response && err.status === 404) {
          return { notFound: true };
        }
      }
    }
  }

  // Handle redirection when sharing app
  if (context.query['shareApp'] === 'true') {
    // Get the user agent from the request headers
    const userAgent = headers['user-agent'] || '';

    // Detect the device info
    const deviceInfo = detectMobileOS(userAgent);

    return {
      redirect: {
        permanent: false,
        destination: deviceInfo.isIOS ? publicRuntimeConfig.shareAppIOSUrl : publicRuntimeConfig.shareAppAndroidUrl,
      },
    };
  }

  return createServerSidePropsResult<ITrackMyParcelServerSideProps, ITrackMyParcelParams>({
    context,
    customContext: {
      serverSideProps: {
        mainTranslation: {},
        trackingTranslation: {},
        notificationTranslation: {},
        pmtPageData: null,
        webVersion: CONNECTING_TO_CONTENTFUL,
      },
      serverRuntimeConfig,
      getServerPropsCached: () => null,
      cacheServerProps: () => null,
      setUrlById: () => null,
    },
    thunks: [getRedisUtils, getServerPropsCached, getPageData],
  });
};

export default TrackMyParcelPage;
